import React, { useCallback, useEffect, useState } from 'react';
import { array, bool, func, shape, string } from 'prop-types';
import { groupBy } from 'lodash';
import useApi from '../../hooks/useApi';
import template from 'url-template';

import TagSelect from '../../components/FormFields/TagSelect';

const BuildingTypesUrl = template.parse('/building-types{?limit}');
const SitesUrl = template.parse('/companies/{companyId}/sites{?limit,sort}');
const ClientsUrl = template.parse('/companies/{companyId}/clients{?limit,sort}');

const LIMIT = 10000;
const SORT = 'name^asc';

const SitesSelectFilter = ({
    companyId,
    disabled,
    sitesFilter,
    onChangeSitesFilter
}) => {
    const [siteData, setSiteData] = useState([]);

    const [buildingTypes, getBuildingTypes] = useApi(() => ({
        url: BuildingTypesUrl.expand({ limit: LIMIT })
    }));

    const [sites, getSites] = useApi(() => ({
        url: SitesUrl.expand({
            companyId,
            limit: LIMIT,
            sort:  SORT
        })
    }));

    const [clients, getClients] = useApi(() => ({
        url: ClientsUrl.expand({
            companyId,
            limit: LIMIT,
            sort:  SORT
        })
    }));

    useEffect(() => {
        getBuildingTypes();
        getSites();
        getClients();

        return () => {
            buildingTypes.cancel();
            sites.cancel();
        };
    }, []);

    useEffect(() => {
        if (sites.data && clients.data) {
            sites.data.forEach(site => {
                clients.data.forEach(client => {
                    if (site.clientId === client.id) {
                        site.name = `${site.name} (${client.name})`;
                    }
                });
            });

            setSiteData(sites.data);
        }
    }, [sites.data, clients.data]);

    const changeHandler = (defaultValues = {}) => {
        return (event) => {
            const { value } = event.target;
            const groupedValues = groupBy(value, 'type');
            const newValues = { ...defaultValues };
            Object.entries(groupedValues).forEach(([type, value]) => {
                newValues[type] = value;
            });

            onChangeSitesFilter(newValues);
        };
    };

    const handleChange = useCallback(
        changeHandler({
            buildingTypes: [],
            sites:         []
        })
    );

    const options = [
        {
            error:     buildingTypes.error,
            isLoading: buildingTypes.loading,
            items:     buildingTypes.data,
            name:      'Building Types',
            itemProps: {
                type: 'buildingTypes'
            }
        },
        {
            error:     sites.error,
            isLoading: sites.loading,
            items:     siteData,
            name:      'Sites',
            itemProps: {
                type: 'sites'
            }
        }
    ];

    const value = [...sitesFilter.buildingTypes,...sitesFilter.sites];

    return (
        <TagSelect
            disabled={disabled}
            grouped
            kickerText="Add any combination of Building Types and Specific Sites to filter by these items."
            label="Sites"
            name={undefined}
            onChange={handleChange}
            options={options}
            placeholder="Add Sites"
            value={value}
        />
    );
};

SitesSelectFilter.defaultProps = {
    companyId:      '',
    disabled:       false,
    onFilterChange: () => {},
    sitesFilter:    {
        buildingTypes: [],
        sites:         []
    }
};

SitesSelectFilter.propTypes = {
    companyId:      string.isRequired,
    disabled:       bool,
    onFilterChange: func.isRequired,
    sitesFilter:    shape({
        buildingTypes: array.isRequired,
        sites:         array.isRequired
    })
};

export default SitesSelectFilter;
